import React from 'react';
import {Button, Popconfirm, Tag} from 'antd';
import {useNavigate} from 'react-router-dom';
import {useMutation} from '@apollo/client';
import type {ColumnsType} from 'antd/es/table';
import {message, Modal} from 'antd';
import {CheckJobType} from '../graphql/types/BcpCheckJobTypes';
import {formatTime} from '../utils/timeFormat';
import {
    DELETE_BCP_CHECK_JOB,
    DISABLE_BCP_CHECK_JOB,
    ENABLE_BCP_CHECK_JOB,
    PUBLISH_BCP_CHECK_JOB,
} from '../graphql/mutations/bcpCheckJobMutations';

interface BcpCheckJobColumnsProps {
    onRefresh: () => void;
}

export const useBcpCheckJobColumns = ({onRefresh}: BcpCheckJobColumnsProps): ColumnsType<CheckJobType> => {
    const navigate = useNavigate();
    const [publishJob] = useMutation(PUBLISH_BCP_CHECK_JOB);
    const [enableJob] = useMutation(ENABLE_BCP_CHECK_JOB);
    const [disableJob] = useMutation(DISABLE_BCP_CHECK_JOB);
    const [deleteJob] = useMutation(DELETE_BCP_CHECK_JOB);

    const handlePublish = (id: number) => {
        Modal.confirm({
            title: '确认发布',
            content: '确认发布该核对任务？',
            onOk: async () => {
                try {
                    const res = await publishJob({variables: {id}});
                    const result = res.data?.publishBcpCheckJob;
                    if (!result?.success) {
                        message.error(result?.message || '发布失败');
                        return;
                    }
                    message.success('发布成功');
                    onRefresh();
                } catch (e: any) {
                    message.error(e?.message || '发布失败');
                }
            },
        });
    };

    const handleToggleStatus = async (record: CheckJobType) => {
        const isEnabled = record.status === 1;
        try {
            if (isEnabled) {
                const res = await disableJob({variables: {id: record.id}});
                const result = res.data?.disableBcpCheckJob;
                if (!result?.success) {
                    message.error(result?.message || '禁用失败');
                    return;
                }
                message.success('禁用成功');
            } else {
                const res = await enableJob({variables: {id: record.id}});
                const result = res.data?.enableBcpCheckJob;
                if (!result?.success) {
                    message.error(result?.message || '启用失败');
                    return;
                }
                message.success('启用成功');
            }
            onRefresh();
        } catch (e: any) {
            message.error(e?.message || '操作失败');
        }
    };

    const handleDelete = async (id: number) => {
        try {
            const res = await deleteJob({variables: {id}});
            const result = res.data?.deleteBcpCheckJob;
            if (!result?.success) {
                message.error(result?.message || '删除失败');
                return;
            }
            message.success('删除成功');
            onRefresh();
        } catch (e: any) {
            message.error(e?.message || '删除失败');
        }
    };

    return [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: '任务名称',
            dataIndex: 'name',
            key: 'name',
            width: 200,
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            render: (status: number, record: CheckJobType) => (
                <Tag color={status === 1 ? 'green' : 'default'}>
                    {record.statusDesc || (status === 1 ? '启用' : '禁用')}
                </Tag>
            ),
        },
        {
            title: '用户组',
            dataIndex: 'userGroupName',
            key: 'userGroupName',
            width: 150,
        },
        {
            title: '当前版本号',
            dataIndex: 'currVersion',
            key: 'currVersion',
            width: 100,
        },
        {
            title: '生效版本号',
            dataIndex: 'validVersion',
            key: 'validVersion',
            width: 100,
        },
        {
            title: '创建人',
            dataIndex: 'createUser',
            key: 'createUser',
            width: 120,
        },
        {
            title: '更新时间',
            dataIndex: 'updateTime',
            key: 'updateTime',
            width: 180,
            render: (t: string) => formatTime(t),
        },
        {
            title: '操作',
            key: 'action',
            width: 280,
            render: (_: any, record: CheckJobType) => (
                <div className="flex flex-wrap gap-1">
                    <Button
                        type="link"
                        size="small"
                        onClick={() => navigate(`/ops-tool/settlement/bcp/check-jobs/${record.id}`)}
                    >
                        详情
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => navigate(`/ops-tool/settlement/bcp/check-jobs/${record.id}/edit`)}
                    >
                        编辑
                    </Button>
                    {record.currVersion !== record.validVersion && (
                        <Button
                            type="link"
                            size="small"
                            onClick={() => handlePublish(record.id!)}
                        >
                            发布
                        </Button>
                    )}
                    <Button
                        type="link"
                        size="small"
                        onClick={() => handleToggleStatus(record)}
                    >
                        {record.status === 1 ? '禁用' : '启用'}
                    </Button>
                    <Popconfirm
                        title="确认删除"
                        description="确认删除该核对任务？此操作不可恢复。"
                        onConfirm={() => handleDelete(record.id!)}
                        okText="确认"
                        cancelText="取消"
                    >
                        <Button type="link" size="small" danger>删除</Button>
                    </Popconfirm>
                </div>
            ),
        },
    ];
};
